import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  Clock, ChefHat, Users, Heart, Bookmark, Share2, Play, CheckCircle2, 
  MapPin, ArrowLeft, Camera, Upload, Flame, AlertCircle, X, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

export default function RecipeDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [cooking, setCooking] = useState(false);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchRecipe(); }, [id]);

  const fetchRecipe = async () => {
    try {
      const response = await api.get(`/recipes/${id}`);
      setRecipe(response.data);
    } catch (error) {
      toast.error('Impossible de charger la recette');
      navigate('/explore');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      const response = await api.post(`/recipes/${id}/like`);
      setRecipe({ 
        ...recipe, 
        is_liked: response.data.liked, 
        likes_count: recipe.likes_count + (response.data.liked ? 1 : -1) 
      });
    } catch (error) { 
      toast.error('Erreur'); 
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selectionne une image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop grande (max 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProofImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitProof = async () => {
    if (!proofImage) {
      toast.error('Ajoute une photo de ta realisation!');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/recipes/${id}/cook`, {
        proof_image: proofImage
      });
      
      toast.success(response.data.message);
      confetti({ 
        particleCount: 100, 
        spread: 70, 
        origin: { y: 0.6 }, 
        colors: ['#1E4ED8', '#60A5FA', '#F8F5F2'] 
      });
      
      setShowProofDialog(false);
      setCooking(false);
      setCompletedSteps([]);
      setProofImage(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const SpiceMeter = ({ level }) => (
    <div className="flex gap-1">
      {Array(5).fill(0).map((_, i) => (
        <span key={i} className={`text-lg ${i < level ? '' : 'opacity-20'}`}>🌶️</span>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Skeleton className="w-full h-80 rounded-3xl mb-6" />
        <Skeleton className="h-8 w-3/4 mb-4" />
      </div>
    );
  }

  if (!recipe) return null;

  const allDone = completedSteps.length === recipe.steps?.length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="recipe-detail-page">
      {/* Back button */}
      <button 
        onClick={() => navigate(-1)} 
        className="flex items-center gap-2 text-gray-500 hover:text-[#1E4ED8] transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </button>

      {/* Hero Image */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="relative aspect-video rounded-3xl overflow-hidden mb-6 shadow-xl"
      >
        <img 
          src={recipe.cover_image || recipe.image_url || 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1200&q=80'} 
          alt={recipe.title} 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2">
          {recipe.is_premium && (
            <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold">
              Premium
            </Badge>
          )}
        </div>
        
        {/* Region */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
          <MapPin className="w-5 h-5" />
          <span className="capitalize font-medium">{recipe.region?.replace('_', ' ')}</span>
        </div>
        
        {/* Stats */}
        <div className="absolute bottom-4 right-4 flex items-center gap-4 text-white">
          <span className="flex items-center gap-1.5 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
            <Heart className="w-4 h-4" />
            {recipe.likes_count}
          </span>
          <span className="flex items-center gap-1.5 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
            <Users className="w-4 h-4" />
            {recipe.cooks_count}
          </span>
        </div>
      </motion.div>

      {/* Title & Actions */}
      <div className="flex justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="font-fraunces text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {recipe.title}
          </h1>
          <p className="text-gray-600 text-lg">{recipe.description}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            className={`rounded-full ${recipe.is_liked ? 'text-[#D9381E] border-[#D9381E]' : ''}`}
            onClick={handleLike}
          >
            <Heart className={`w-5 h-5 ${recipe.is_liked ? 'fill-current' : ''}`} />
          </Button>
          <Button variant="outline" size="icon" className="rounded-full">
            <Bookmark className="w-5 h-5" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-full">
            <Share2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-5 gap-3 mb-8">
        <Card className="card-tunisia p-4 text-center">
          <SpiceMeter level={recipe.spice_level} />
          <p className="text-xs text-gray-500 mt-1">Piment</p>
        </Card>
        <Card className="card-tunisia p-4 text-center">
          <p className="font-fraunces text-2xl font-bold text-[#1E4ED8]">{recipe.prep_time_minutes}m</p>
          <p className="text-xs text-gray-500">Préparation</p>
        </Card>
        <Card className="card-tunisia p-4 text-center">
          <p className="font-fraunces text-2xl font-bold text-[#D9381E]">{recipe.cook_time_minutes}m</p>
          <p className="text-xs text-gray-500">Cuisson</p>
        </Card>
        <Card className="card-tunisia p-4 text-center">
          <p className="font-fraunces text-2xl font-bold text-[#4F6F52]">{recipe.servings}</p>
          <p className="text-xs text-gray-500">Portions</p>
        </Card>
        <Card className="card-tunisia p-4 text-center">
          <p className="font-fraunces text-2xl font-bold text-[#E07A5F]">{recipe.difficulty}/5</p>
          <p className="text-xs text-gray-500">Difficulté</p>
        </Card>
      </div>

      {/* Creator Card */}
      <Card className="card-tunisia p-4 mb-8">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer group" 
            onClick={() => navigate(`/u/${recipe.creator_username}`)}
          >
            <Avatar className="w-12 h-12 border-2 border-[#1E4ED8]/20">
              <AvatarImage src={recipe.creator_profile_picture} />
              <AvatarFallback className="bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] text-white">
                {recipe.creator_username?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-bold group-hover:text-[#1E4ED8] transition-colors">
                {recipe.creator_display_name}
              </p>
              <p className="text-sm text-gray-500">@{recipe.creator_username}</p>
            </div>
          </div>
          <Button 
            variant="outline"
            className="rounded-full"
            onClick={() => navigate(`/u/${recipe.creator_username}`)}
          >
            Voir le profil
          </Button>
        </div>
      </Card>

      {/* Ingredients & Instructions */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Ingredients */}
        <div>
          <h2 className="font-fraunces text-2xl font-bold mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#4F6F52] flex items-center justify-center">
              <ChefHat className="w-4 h-4 text-white" />
            </span>
            Ingrédients
          </h2>
          <Card className="card-tunisia">
            <CardContent className="p-5">
              <ul className="space-y-3">
                {recipe.ingredients?.map((ing, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#1E4ED8] mt-2 flex-shrink-0" />
                    <span>
                      <strong>{ing.amount} {ing.unit}</strong> {ing.name}
                      {ing.is_optional && (
                        <span className="text-gray-400 ml-2 text-sm">(optionnel)</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-fraunces text-2xl font-bold flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#D9381E] flex items-center justify-center">
                <Play className="w-4 h-4 text-white" />
              </span>
              Instructions
            </h2>
            {!cooking && (
              <Button 
                className="bg-[#1E4ED8] hover:bg-[#1E40AF] text-white rounded-full" 
                onClick={() => setCooking(true)}
              >
                <Play className="mr-2 w-4 h-4" />
                Commencer
              </Button>
            )}
          </div>
          
          <div className="space-y-4">
            {recipe.steps?.map((step, i) => (
              <Card 
                key={i} 
                className={`card-tunisia p-4 cursor-pointer transition-all ${
                  completedSteps.includes(i) 
                    ? 'bg-[#4F6F52]/10 border-[#4F6F52]' 
                    : cooking ? 'hover:border-[#1E4ED8]' : ''
                }`}
                onClick={() => {
                  if (cooking) {
                    setCompletedSteps(
                      completedSteps.includes(i) 
                        ? completedSteps.filter(s => s !== i) 
                        : [...completedSteps, i]
                    );
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    completedSteps.includes(i) 
                      ? 'bg-[#4F6F52] text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {completedSteps.includes(i) 
                      ? <CheckCircle2 className="w-5 h-5" /> 
                      : <span className="font-bold">{i + 1}</span>
                    }
                  </div>
                  <p className={completedSteps.includes(i) ? 'line-through text-gray-400' : ''}>
                    {step.instruction}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Complete Button - Now Opens Proof Dialog */}
          {cooking && allDone && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="mt-6"
            >
              <Card className="bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] p-6 rounded-2xl text-white">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Camera className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-fraunces text-xl font-bold">Bravo, tu as terminé!</h3>
                    <p className="text-white/80 text-sm mt-1">
                      Partage une photo de ta réalisation pour gagner tes BARKA
                    </p>
                  </div>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-yellow-300" />
                      <span className="font-bold">+5 BARKA</span>
                      <span className="text-white/60 text-sm">immédiat</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-yellow-300" />
                      <span className="font-bold">+45 BARKA</span>
                      <span className="text-white/60 text-sm">après validation</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
                  <AlertCircle className="w-4 h-4" />
                  <span>La communauté validera ta réalisation pour éviter les abus</span>
                </div>
                
                <Button 
                  className="w-full bg-white text-[#1E4ED8] hover:bg-white/90 h-12 text-lg rounded-full font-bold"
                  onClick={() => setShowProofDialog(true)}
                >
                  <Camera className="mr-2 w-5 h-5" />
                  Ajouter ma photo
                </Button>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Proof Upload Dialog */}
      <Dialog open={showProofDialog} onOpenChange={setShowProofDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-fraunces text-xl">Preuve de réalisation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Prends une photo de ton plat terminé. La communauté validera ta réalisation 
              pour que tu reçoives tes BARKA.
            </p>

            {proofImage ? (
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <img 
                  src={proofImage} 
                  alt="Ma réalisation" 
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setProofImage(null)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-square rounded-2xl border-2 border-dashed border-[#1E4ED8]/30 flex flex-col items-center justify-center gap-3 hover:border-[#1E4ED8] hover:bg-[#1E4ED8]/5 transition-all"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#1E4ED8]/10 flex items-center justify-center">
                  <Image className="w-8 h-8 text-[#1E4ED8]" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-700">Clique pour ajouter ta photo</p>
                  <p className="text-sm text-gray-500">JPG, PNG jusqu'à 10MB</p>
                </div>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="bg-[#F8F5F2] rounded-xl p-4">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#D9381E]" />
                Règles anti-abus
              </h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• La photo doit montrer clairement ton plat</li>
                <li>• Pas de photos trouvées sur internet</li>
                <li>• Une seule validation par recette par 24h</li>
                <li>• La communauté vote pour valider</li>
              </ul>
            </div>

            <Button
              onClick={handleSubmitProof}
              disabled={!proofImage || submitting}
              className="w-full bg-[#1E4ED8] hover:bg-[#1E40AF] text-white rounded-full h-12"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Soumettre ma réalisation
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
